/**
 * Compositor layer promotion for the scroll-driven wrappers.
 *
 * framer builds the `transform` string from whichever values it is driving and
 * emits the literal `none` whenever they are all sitting at their defaults —
 * which every scrubbed reveal here is at both ends of its range, since they rest
 * at `y: 0`. Two things follow from that, and both cost frames:
 *
 * - A plain `translateY()` is not enough to earn an element its own compositor
 *   layer. So a drifting card is repainted into the page's scrolling surface on
 *   every frame it moves, and on a card that is mostly a full-quality photo that
 *   is a photo-sized re-raster per frame.
 * - `none` and a real transform are not the same kind of box: a transform makes
 *   the element a stacking context and a containing block for fixed descendants.
 *   Toggling between the two at each end of a range makes the browser tear that
 *   down and build it again on the way back.
 *
 * Appending `translateZ(0)` settles both at once — the string is never `none`,
 * and a 3D transform is the explicit, engine-independent signal to promote,
 * rather than relying on a 2D heuristic that varies by browser version. The
 * element is rasterised once and moved by the compositor from then on.
 */

/**
 * Promotion is not free: every layer costs GPU memory proportional to its area,
 * and promoting everything is a well-known way to make a page slower rather than
 * faster. Two rules keep the count honest, and every call site follows both:
 *
 * - Only wrappers that move for a whole section crossing, never ones that move
 *   for a brief reveal and then sit still.
 * - Gated on the same flag as the drift they carry, so the layouts below `lg` —
 *   where the drift is switched off entirely — promote nothing at all.
 *
 * `transform` alone creates a stacking context but *not* an isolated group (the
 * compositing spec lists `clip-path`, `filter`, `mask` and `opacity` for that,
 * not `transform`), so this is safe to put on a sibling of an element relying on
 * `mix-blend-mode` to read its backdrop.
 *
 * The one visible trade-off is text: Chromium turns off subpixel antialiasing
 * inside a composited layer that has no opaque background of its own, so a
 * caption riding one of these wrappers renders with greyscale antialiasing on
 * the platforms that still use LCD text. Which is why the text-only drifts — the
 * hero's copy column, the timeline's paragraph column — deliberately do not get
 * this: they carry no photo, so the raster they save is small and the cost lands
 * entirely on the type.
 *
 * The first argument is framer's parsed transform bag. Its type isn't exported
 * from the package, and this never reads it, so it is left as `unknown` — which
 * is assignable to the narrower parameter framer declares.
 */
export const promoteLayer = (_: unknown, generated: string) =>
  generated ? `${generated} translateZ(0)` : "translateZ(0)";
